import { useState } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import EntraIdAuth from './EntraIdAuth';
import { getStore, resetStore } from './store/store';
import { Provider } from 'react-redux';
import { isFirefox, isSafari } from 'react-device-detect';
import * as Sentry from '@sentry/react';
import * as Msal from '@azure/msal-browser';
import { Auth0Provider, useAuth0, User } from '@auth0/auth0-react';
import { Auth0ProviderOptions } from '@auth0/auth0-react/src/auth0-provider';
import { AuthProvider as OidcAuthProvider, useAuth } from 'react-oidc-context';
import * as microsoftTeams from '@microsoft/teams-js';

// Third party styles
import './vendors.scss';

import { storeSsoToken } from 'actions/teamsActions';
import { loginRequest, msalConfig } from 'components/Auth/MSALAuthObjects';
import TeamsAppErrorDialog from 'components/Auth/TeamsAppErrorDialog';
import UserAuthorizationFailedDialog from 'components/Auth/UserAuthorizationFailedDialog';
import {
  BrowserAuthProvider,
  BrowserAuthContextValue,
} from './providers/BrowserAuthProvider';
import parseJWT from 'utils/parseJWT';
import { LoginPageButton } from './LoginPageButton';
import { validateIapRouteStructure } from 'utils/iapParamValidation';

import {
  AUTH_0_AUDIENCE,
  AUTH_0_CLIENT_ID,
  AUTH_0_DOMAIN,
  BROWSER_AUTH_PROVIDER,
  DESCOPE_BASE_URL,
  DESCOPE_FLOW_ID,
  DESCOPE_PROJECT_ID,
} from './environment';
import './instrument';

const msalInstance = new Msal.PublicClientApplication(msalConfig);
let currentAccounts = msalInstance.getAllAccounts();

const root = ReactDOM.createRoot(document.getElementById('root')!);
const isBrave = async () => {
  return (navigator.brave && (await navigator.brave.isBrave())) || false;
};
const browserAuthProvider: BrowserAuthContextValue['authProvider'] =
  BROWSER_AUTH_PROVIDER === 'descope'
    ? 'descope'
    : BROWSER_AUTH_PROVIDER === 'msal'
      ? 'legacy-msal'
      : 'auth0';

// IMPORTANT: Auth notes
// Auth is very complex with Microsoft and our Teams integration.  Following the steps of action is essential to understanding what is going on
// 1: Check if app is running in browser or Teams.
// 1.1: If teams, render login page with function to use SSO to log in to Teams
// 1.2: If not teams...
// 1.2.1: Check to see if any accounts are known to the browser with msalInstance.getAllAccounts()
// 1.2.1.1: If valid accounts, add account as loginHint to ssoRequest, attempt acquireTokenSilent -> If no accounts or request fail, render login button
// 1.2.2: Login button click => redirect

let teamsInitialized = false;

const getTokenExpiration = (token: string | undefined): string | undefined => {
  const parsedToken = parseJWT(token);
  const exp = parsedToken?.exp;

  if (typeof exp === 'number') {
    return new Date(exp * 1000).toISOString();
  }

  if (typeof exp === 'string' && !Number.isNaN(Number(exp))) {
    return new Date(Number(exp) * 1000).toISOString();
  }

  const expiresOn = parsedToken?.expiresOn;
  return typeof expiresOn === 'string' ? expiresOn : undefined;
};

const getStringClaim = (
  claims: Record<string, unknown> | null | undefined,
  claimKey: string
): string | undefined => {
  const claimValue = claims?.[claimKey];
  return typeof claimValue === 'string' ? claimValue : undefined;
};

const rememberInitialPath = () => {
  if (window.location.pathname !== '/') {
    sessionStorage.initialPath = validatedPath();
  }
};

const initLegacyMsalLogin = async (
  instance: Msal.PublicClientApplication = msalInstance
) => {
  sessionStorage['hasAgreedToTerms'] = true;
  await instance.initialize();

  instance
    .loginRedirect(loginRequest)
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    .then(() => {})
    .catch((error: any) => {
      console.warn('REDIRECT ERROR: ', error);
    });
};

const logoutLegacyMsal = async () => {
  try {
    const logoutMsalInstance = new Msal.PublicClientApplication(msalConfig);
    await logoutMsalInstance.initialize();
    await logoutMsalInstance.logoutRedirect({
      onRedirectNavigate: () => false,
    });
    window.location.assign('/logout');
  } catch (error) {
    console.error('[LOGOUT] Legacy MSAL logout failed:', error);
    window.location.assign('/logout');
  } finally {
    sessionStorage.clear();
    localStorage.clear();
    resetStore();
  }
};

const legacyMsalBrowserAuthValue: BrowserAuthContextValue = {
  authProvider: 'legacy-msal',
  login: () => initLegacyMsalLogin(msalInstance),
  logout: logoutLegacyMsal,
  getAccessToken: async () => sessionStorage['accessToken'] || undefined,
};

const renderAuthenticatedApp = (
  finalAuthInfo: Record<string, unknown>,
  authValue: BrowserAuthContextValue
) => {
  return (
    <BrowserAuthProvider value={authValue}>
      <Provider
        store={getStore({
          auth: finalAuthInfo,
        })}
      >
        <App />
      </Provider>
    </BrowserAuthProvider>
  );
};

const renderLoginPage = (
  authValue: BrowserAuthContextValue,
  teamsInitialized: boolean,
  props: { error?: Error; user?: User } = {}
) => {
  return (
    <BrowserAuthProvider value={authValue}>
      <LoginPageButton isUsingTeams={teamsInitialized} {...props} />
    </BrowserAuthProvider>
  );
};

function initTeamsAuth() {
  microsoftTeams.authentication
    .getAuthToken()
    .then((result) => {
      renderAppInTeams(result);
    })
    .catch((error) => {
      // Teams SDK wraps errors as new Error(object) — message becomes "[object Object]".
      // Try to extract the real error from common SDK error shapes.
      let detail = error?.errorCode || error?.cause;
      if (!detail && error?.message === '[object Object]') {
        // The SDK stringified an object as the message; try to find the original
        try {
          detail = JSON.stringify(error, Object.getOwnPropertyNames(error));
        } catch {
          detail = 'Unknown Teams SDK error';
        }
      } else {
        detail = detail || error?.message || error;
      }
      console.error('getAuthToken failed:', detail);
      renderTeamsAppError(error);
    });
}

// Wrap Teams SDK init in a promise that resolves to true (Teams/Outlook hub)
// or false (browser / non-M365 host). A 2s timeout prevents blocking the app
// if the SDK hangs (e.g. in a plain browser where it never resolves).
const teamsInitPromise = new Promise<boolean>((resolve) => {
  const timeout = setTimeout(() => resolve(false), 2000);
  microsoftTeams.app
    .initialize()
    .then(() => {
      clearTimeout(timeout);
      teamsInitialized = true;
      console.log('Teams initialized:', teamsInitialized);
      resolve(true);
    })
    .catch((error) => {
      clearTimeout(timeout);
      console.error('Teams initialization failed:', error);
      resolve(false);
    });
});

const auth0Domain = AUTH_0_DOMAIN;

const auth0ClientId = AUTH_0_CLIENT_ID;

const auth0Audience = AUTH_0_AUDIENCE;

const providerConfig: Auth0ProviderOptions = {
  domain: auth0Domain,
  clientId: auth0ClientId,
  authorizationParams: {
    redirect_uri: window.location.origin,
    audience: auth0Audience,
  },
  // useRefreshTokens: true,
  cacheLocation: 'memory',
};

async function main(): Promise<void> {
  // Wait for Teams SDK to either initialize or time out before choosing auth path.
  // This prevents the race where Auth0 renders first and then Teams SSO overwrites it.
  const isInTeamsHost = await teamsInitPromise;

  if (isInTeamsHost) {
    // Teams/Outlook hub — use SSO
    initTeamsAuth();
  } else if (browserAuthProvider === 'legacy-msal' && !teamsInitialized) {
    await msalInstance.initialize();

    // TODO: We should try to await the conclusion of the "loginRedirect"
    // rather than call this in parallel with the "handleRedirectPromise()".
    if (sessionStorage['hasAgreedToTerms']) {
      msalInstance
        .loginRedirect(loginRequest)
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        .then(() => {})
        .catch((error: any) => {
          // handle error
          console.warn('REDIRECT ERROR: ', error);
        });
    }

    const redirectResponse = await msalInstance.handleRedirectPromise();

    if (redirectResponse !== null) {
      const { accessToken } = redirectResponse;

      if (!accessToken) {
        console.log("We don't have the token yet.");
      } else {
        currentAccounts = msalInstance.getAllAccounts();
        renderAppWithoutAuth0(redirectResponse, currentAccounts);
      }
    } else {
      root.render(
        <BrowserAuthProvider value={legacyMsalBrowserAuthValue}>
          <>
            <EntraIdAuth />
            <LoginPageButton isUsingTeams={teamsInitialized} />
          </>
        </BrowserAuthProvider>
      );
    }
  } else if (browserAuthProvider === 'descope') {
    const descopeBaseUrl = (
      DESCOPE_BASE_URL || 'https://api.descope.com'
    ).replace(/\/$/, '');

    const oidcConfig = {
      authority: `${descopeBaseUrl}/${DESCOPE_PROJECT_ID}`,
      client_id: DESCOPE_PROJECT_ID as string,
      redirect_uri: window.location.origin,
      scope: 'openid email profile',
      extraQueryParams: DESCOPE_FLOW_ID
        ? { flow: DESCOPE_FLOW_ID as string, bg: '#000000', style: 'pratus' }
        : undefined,
    };

    root.render(
      <OidcAuthProvider {...oidcConfig}>
        <OidcDescopeInitApp teamsInitialized={teamsInitialized} />
      </OidcAuthProvider>
    );
  } else {
    const useRefreshTokens =
      isSafari || isFirefox || (await isBrave()) ? true : false;
    // Use localstorage in dev so tokens survive HMR/reloads; memory is lost on each reload
    const useLocalStorage = useRefreshTokens || import.meta.env.DEV;
    providerConfig['useRefreshTokens'] = useRefreshTokens;
    providerConfig['cacheLocation'] = useLocalStorage
      ? 'localstorage'
      : 'memory';

    root.render(
      <Auth0Provider {...providerConfig}>
        <Auth0InitApp teamsInitialized={teamsInitialized} />
      </Auth0Provider>
    );
  }
}

void main();

function validatedPath() {
  const pathname = window.location.pathname;
  const searchParams = Object.fromEntries(
    new URLSearchParams(window.location.search)
  );

  const result = validateIapRouteStructure(pathname, searchParams);
  const isValid = result.success;

  if (isValid) {
    const searchString = window.location.search;
    return searchString ? `${pathname}${searchString}` : pathname;
  } else {
    return pathname;
  }
}

type InitAppProps = {
  teamsInitialized: boolean;
};

function Auth0InitApp({ teamsInitialized }: InitAppProps) {
  rememberInitialPath();

  const {
    isLoading,
    isAuthenticated,
    error,
    user,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = useAuth0();

  const browserAuthValue: BrowserAuthContextValue = {
    authProvider: 'auth0',
    login: async () => {
      await loginWithRedirect();
    },
    logout: async () => {
      try {
        await logout({ logoutParams: { returnTo: window.location.origin } });
      } catch (error) {
        console.error('[LOGOUT] Auth0 logout failed:', error);
        window.location.assign('/');
      } finally {
        sessionStorage.clear();
        localStorage.clear();
        resetStore();
      }
    },
    getAccessToken: async () => getAccessTokenSilently(),
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return renderLoginPage(browserAuthValue, teamsInitialized, { error });
  }

  if (teamsInitialized) {
    initTeamsAuth();
    return <div>Loading...</div>;
  }

  if (isAuthenticated) {
    const authProvider = !!user && user?.sub?.split('|')[0];

    if (!user || (!user?.email_verified && authProvider === 'auth0')) {
      return renderLoginPage(browserAuthValue, teamsInitialized, { user });
    }

    if (
      !user?.email ||
      typeof user.email !== 'string' ||
      user.email.trim() === ''
    ) {
      return (
        <BrowserAuthProvider value={browserAuthValue}>
          <UserAuthorizationFailedDialog
            show={true}
            onClose={() => {}}
            title="Email Required"
            message="We signed you in, but your account does not have an email address associated with it. PRATUS needs an email to create or load your user. Please contact your administrator, or try a different account."
          />
        </BrowserAuthProvider>
      );
    }

    const finalAuthInfo = {
      teamsInitialized: teamsInitialized || false,
      account: {
        userName: user?.email,
      },
      type: 'AUTH0',
    };

    return renderAuthenticatedApp(finalAuthInfo, browserAuthValue);
  }

  return renderLoginPage(browserAuthValue, teamsInitialized);
}

function OidcDescopeInitApp({ teamsInitialized }: InitAppProps) {
  rememberInitialPath();

  const oidc = useAuth();
  const [fallbackExpiresOn] = useState(() =>
    new Date(Date.now() + 60 * 60 * 1000).toISOString()
  );

  const browserAuthValue: BrowserAuthContextValue = {
    authProvider: 'descope',
    login: async () => {
      await oidc.signinRedirect();
    },
    logout: async () => {
      try {
        await oidc.signoutRedirect({
          post_logout_redirect_uri: window.location.origin,
        });
      } catch (error) {
        console.error('[LOGOUT] Descope logout failed:', error);
        window.location.assign('/');
      } finally {
        sessionStorage.clear();
        localStorage.clear();
        resetStore();
      }
    },
    getAccessToken: async () => oidc.user?.access_token ?? undefined,
  };

  if (oidc.isLoading) {
    return <div>Loading...</div>;
  }

  if (oidc.error) {
    console.error('OIDC auth error:', oidc.error);
    return renderLoginPage(browserAuthValue, teamsInitialized, {
      error: oidc.error,
    });
  }

  if (teamsInitialized) {
    initTeamsAuth();
    return <div>Loading...</div>;
  }

  if (oidc.isAuthenticated && oidc.user) {
    const accessToken = oidc.user.access_token;
    const idToken = oidc.user.id_token;
    const tokenForClaims = accessToken || idToken;
    const tokenClaims = tokenForClaims ? parseJWT(tokenForClaims) : null;

    const userName =
      getStringClaim(tokenClaims, 'email') ||
      getStringClaim(tokenClaims, 'email_address') ||
      getStringClaim(tokenClaims, 'preferred_username') ||
      oidc.user.profile?.email;

    if (!userName || typeof userName !== 'string' || userName.trim() === '') {
      return (
        <BrowserAuthProvider value={browserAuthValue}>
          <UserAuthorizationFailedDialog
            show={true}
            onClose={() => {}}
            title="Email Required"
            message="We signed you in, but your account does not have an email address associated with it. PRATUS needs an email to create or load your user. Please contact your administrator, or try a different account."
          />
        </BrowserAuthProvider>
      );
    }

    const finalAuthInfo = {
      accessToken: accessToken,
      expiresOn: getTokenExpiration(accessToken) || fallbackExpiresOn,
      teamsInitialized: teamsInitialized || false,
      account: {
        userName,
      },
      type: 'DESCOPE',
    };

    sessionStorage.setItem('accessToken', accessToken ?? '');
    sessionStorage.setItem('accessTokenExpiresOn', finalAuthInfo.expiresOn);

    return renderAuthenticatedApp(finalAuthInfo, browserAuthValue);
  }

  return renderLoginPage(browserAuthValue, teamsInitialized);
}

const renderAppWithoutAuth0 = (
  authInfo: Msal.AuthenticationResult,
  accounts: Msal.AccountInfo[] = []
) => {
  const email_address = accounts[0]?.username;
  const finalAuthInfo: any = {
    ...authInfo,
    teamsInitialized: teamsInitialized ?? false,
    msalInstance: msalInstance,
    type: 'MSAL',
    account: {
      ...authInfo.account,
      userName: email_address,
    },
  };

  sessionStorage.setItem('isUsingTeams', 'false');

  root.render(
    renderAuthenticatedApp(finalAuthInfo, legacyMsalBrowserAuthValue)
  );
};

const renderAppInTeams = (authInfo: string) => {
  const parsedID = parseJWT(authInfo);
  // Note: Some M365 mobile client apps return a non-JWT "token" even when getAuthToken() resolves.
  // Throwing here ensures we land in the getAuthToken().catch(...) and show TeamsAppErrorDialog.
  if (!parsedID) {
    throw new Error(
      'interaction_required: getAuthToken returned non-JWT token'
    );
  }
  const finalAuthInfo: any = {
    account: {
      userName: parsedID.preferred_username,
    },
    accessToken: authInfo,
    teamsInitialized: true,
    expiresOn:
      getTokenExpiration(authInfo) ||
      new Date(Date.now() + 60 * 60 * 1000).toISOString(),
    msalInstance: msalInstance,
    type: 'MSAL',
  };

  sessionStorage.setItem('accessToken', authInfo);
  sessionStorage.setItem('accessTokenExpiresOn', finalAuthInfo.expiresOn);
  sessionStorage.setItem('isUsingTeams', 'true');

  // Initialize the Redux store with auth info
  const store = getStore({
    auth: finalAuthInfo,
  });

  // Always store the SSO token on Teams app load/refresh
  const aadObjectId = parsedID.oid;
  storeSsoToken(aadObjectId, authInfo);

  // Render the app now that everything is ready
  root.render(
    <BrowserAuthProvider value={legacyMsalBrowserAuthValue}>
      <Provider store={store}>
        <App />
      </Provider>
    </BrowserAuthProvider>
  );
};

const renderTeamsAppError = (error: Error) => {
  Sentry.captureException(error);
  root.render(<TeamsAppErrorDialog show={true} error={error} />);
};
